import { createSelector } from '@reduxjs/toolkit';
import { dexColdWalletApi, dexColdWalletApiEndpoints } from '@skynet/data-dex-cold-wallets';
import type { JsonBalanceAsset } from 'src/store/shared/balances/types';
import type { WalletAsset } from './types';
import type {
  BalanceHistoryMap,
  BalanceHistoryState,
  JsonBalanceHistoryData,
} from 'src/store/shared/balance-history/types';
import dayjs from '@skynet/formatting-date';
import { AssetType } from '@skynet/types-asset-type';
import {
  selectComparisonEndDate,
  selectComparisonStartDate,
} from 'src/store/shared/balance-history/selectors';
import { selectTokenMapData } from 'src/store/shared/tokenMap/selectors';
import type { TokenTypeMap } from 'src/store/shared/cold-wallets/types';
import { dexTokenMapApi } from '@skynet/data-dex-token-map';
import { parseWallet } from 'src/store/utils/wallet';
import { hasProperty } from 'src/utils/type-guards';

const DEX_USD_QUOTE_PRICE = 1;

const isWalletAssetsStructure = (data: unknown): data is { assets: WalletAsset[] } => {
  return hasProperty(data, 'assets') && Array.isArray(data.assets);
};

export interface Store {
  [dexColdWalletApi.reducerPath]: ReturnType<typeof dexColdWalletApi.reducer>;
  balanceHistory: BalanceHistoryState;
  [dexTokenMapApi.reducerPath]: ReturnType<typeof dexTokenMapApi.reducer>;
}

export const getColdWalletsSelectors = <S extends object>(
  selectTarget: (state: S) => string,
  selectTokenTypeMap: (state: S) => TokenTypeMap,
) => {
  const selectColdWallets = createSelector(
    selectTarget,
    (state: S & Store) => state.dexColdWalletApi,
    selectTokenTypeMap,
    (state: S & Store) => selectTokenMapData(state),
    (loanId, walletState, tokenTypeMap, tokensMap) => {
      if (!loanId) return [];
      const { data, error } = dexColdWalletApiEndpoints.listColdWallets.select({
        projectId: +loanId,
      })({
        dexColdWalletApi: walletState,
      });

      const coldWallets = parseWallet(data, error);

      return coldWallets.map((wallet) => {
        const formattedBalances: Record<string, JsonBalanceAsset> = {};

        try {
          const parsedData: unknown = JSON.parse(wallet.balances ?? '{}');
          const parsedBalances = isWalletAssetsStructure(parsedData) ? parsedData : { assets: [] };
          if (Array.isArray(parsedBalances.assets)) {
            parsedBalances.assets.forEach((assets) => {
              const token = tokensMap[assets.symbol] || assets.symbol;

              if (formattedBalances[token]) {
                const existingVolume = formattedBalances[token].volume;
                const newVolume = assets.balance;
                const totalVolume = existingVolume + newVolume;

                formattedBalances[token].lastPrice =
                  totalVolume > 0
                    ? (formattedBalances[token].lastPrice * existingVolume +
                        assets.price * newVolume) /
                      totalVolume
                    : assets.price;

                formattedBalances[token].volume += assets.balance;
                formattedBalances[token].volumeUSD += assets.balanceUsd;
                return;
              }

              formattedBalances[token] = {
                token,
                entryPrice: 0,
                lastPrice: assets.price,
                quotePrice: DEX_USD_QUOTE_PRICE,
                volume: assets.balance,
                volumeUSD: assets.balanceUsd,
                type: tokenTypeMap[token] ?? 2,
                assetType: AssetType.Spot,
                positionType: null,
                unrealizedPnL: 0,
                marginBalance: 0,
                isDoubling: false,
                isSmallBalance: false,
                chain: assets?.blockchain,
                leverage: 1,
              };
            });
          }
        } catch (error) {
          console.error('Error parsing wallet balances', error);
        }

        return {
          account: {
            name: wallet.id.toString(),
            label: wallet.name ?? '',
            status: 1,
            group: 0,
            exchange: 0,
            message: '',
            proxy: '',
          },
          accountID: wallet.id.toString(),
          accountName: wallet.name ?? 'Wallet',
          isWallet: true,
          assets: Object.values(formattedBalances),
          exchangeID: wallet.projectId + 10000000000,
          exchangeName: Object.values(formattedBalances)[0]?.chain,
          updated: dayjs(wallet.updated).format('YYYY-MM-DD HH:mm'),
        };
      });
    },
  );

  const selectWalletsSnapshots = createSelector(
    selectTarget,
    (state: S & Store) => state.dexColdWalletApi,
    (state: S & Store) => selectComparisonStartDate(state),
    (state: S & Store) => selectComparisonEndDate(state),
    selectColdWallets,
    selectTokenTypeMap,
    (state: S & Store) => selectTokenMapData(state),
    (
      loanId,
      walletState,
      startDate,
      endDate,
      coldWallets,
      tokenTypeMap,
      tokensMap,
    ): BalanceHistoryMap => {
      if (!loanId) return {};

      const formatedStartDate = dayjs(startDate).format('YYYY-MM-DD');
      const formatedEndDate = dayjs(endDate).format('YYYY-MM-DD');
      const snapshotsResult = dexColdWalletApiEndpoints.listProjectSnapshots.select({
        projectId: +loanId,
        startDate: formatedStartDate,
        endDate: formatedEndDate,
      })({ dexColdWalletApi: walletState });

      const snapshotsData = snapshotsResult?.data;

      if (!snapshotsData) return {};

      const formattedSnapshots = snapshotsData.data.reduce<BalanceHistoryMap>((acc, snapshot) => {
        try {
          const rawBalances = typeof snapshot.balances === 'string' ? snapshot.balances : '{}';
          const parsedData: unknown = JSON.parse(rawBalances);
          const parsedBalances = isWalletAssetsStructure(parsedData) ? parsedData : { assets: [] };
          const accountId = snapshot.walletId.toString();
          const walletDate = dayjs.utc(snapshot.date);
          const isToday = walletDate.isSame(dayjs.utc(), 'day');
          const date = isToday
            ? walletDate.format('YYYY-MM-DDTHH:mm:ss')
            : walletDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss');

          const tokens: Record<string, JsonBalanceHistoryData> = {};

          if (Array.isArray(parsedBalances.assets)) {
            parsedBalances.assets.forEach((asset) => {
              const token = tokensMap[asset.symbol] || asset.symbol;

              if (!tokens[token]) {
                tokens[token] = {
                  token,
                  lastPrice: asset.price,
                  assetType: AssetType.Spot,
                  positionType: null,
                  unrealizedPnL: 0,
                  marginBalance: 0,
                  leverage: 1,
                  volume: asset.balance,
                  type: tokenTypeMap[token] ?? 2,
                  entryPrice: 0,
                  quotePrice: 1,
                };
                return;
              }

              const existingVolume = tokens[token].volume;
              const existingPrice = tokens[token].lastPrice;
              const newVolume = asset.balance;
              const totalVolume = existingVolume + newVolume;

              tokens[token].lastPrice =
                totalVolume > 0
                  ? (existingPrice * existingVolume + asset.price * newVolume) / totalVolume
                  : asset.price;

              tokens[token].volume += asset.balance;
            });
          }

          if (!acc[accountId]) {
            acc[accountId] = {};
          }
          acc[accountId][date] = Object.values(tokens);
        } catch (error) {
          console.error('Error parsing snapshot balances', error);
        }

        return acc;
      }, {});

      return Object.entries(formattedSnapshots).reduce<BalanceHistoryMap>(
        (acc, [accountId, item]) => {
          const wallet = coldWallets.find((w) => w.accountID === accountId);

          if (!wallet) {
            return {
              ...acc,
              [accountId]: item,
            };
          }

          const date = dayjs.utc(wallet.updated).format('YYYY-MM-DDTHH:mm:ss');
          if (!item[date]) {
            item[date] = wallet.assets.map(
              (asset) =>
                ({
                  token: asset.token,
                  lastPrice: asset.lastPrice,
                  assetType: asset.assetType,
                  positionType: asset.positionType,
                  unrealizedPnL: asset.unrealizedPnL,
                  marginBalance: asset.marginBalance,
                  leverage: asset.leverage,
                  volume: asset.volume,
                  type: asset.type,
                  entryPrice: asset.entryPrice,
                  quotePrice: asset.quotePrice ?? DEX_USD_QUOTE_PRICE,
                }) satisfies JsonBalanceHistoryData,
            );

            return {
              ...acc,
              [accountId]: item,
            };
          }

          return acc;
        },
        {},
      );
    },
  );

  return { selectColdWallets, selectWalletsSnapshots };
};
